import { cssInterop } from 'nativewind';
import { memo } from 'react';
import { cn } from '../../utils/cn';
import { isWeb } from '../../utils/platform';
import { IconSizeValue, LucideIconName, resolveIconSize } from './icon';
import { lucideIconsMap } from './lucideIconMap';

interface Props {
  name: LucideIconName;
  size?: IconSizeValue;
  strokeWidth?: number;
  color?: string;
  absoluteStrokeWidth?: boolean;
  className?: string;
}

const createInteropIcon = (name: LucideIconName) =>
  cssInterop(lucideIconsMap[name], {
    className: {
      target: 'style',
      nativeStyleToProp: {
        color: true,
        width: true,
        height: true,
        stroke: true,
        fill: true,
        opacity: true
      }
    }
  });

// `cssInterop` returns a brand-new component type on every call, so each icon must be wrapped exactly
// once — doing it during render would remount the icon every render (new component identity) and trips
// react-hooks/immutability+purity. Wrap every icon up front at module scope, then render just looks up
// the stable wrapper by name.
const interopIconMap = Object.fromEntries(
  (Object.keys(lucideIconsMap) as LucideIconName[]).map((name) => [name, createInteropIcon(name)])
) as Record<LucideIconName, ReturnType<typeof createInteropIcon>>;

export const ReactNativeLucideIcon = memo(
  ({ name, size = 'small', strokeWidth, color, absoluteStrokeWidth, className }: Props) => {
    const ReactNativeLucideIconComponent = interopIconMap[name];
    const iconSize = resolveIconSize(size);

    return (
      <ReactNativeLucideIconComponent
        size={iconSize}
        strokeWidth={strokeWidth}
        color={color ?? (isWeb ? 'currentColor' : 'black')}
        absoluteStrokeWidth={absoluteStrokeWidth}
        className={cn(className, 'shrink-0')}
      />
    );
  }
);

ReactNativeLucideIcon.displayName = 'ReactNativeLucideIcon';
